import { useCallback, useEffect } from "react";
import { useTabId } from "./tab-id";
import { fixString } from "@monta/router-js";
import Toast from "./toast";
import type { AxiosError } from "axios";
import type { TUtils } from ".";
import { UsersTypeEnum } from "./enums";
import { useEvery } from "./polling";
import type { TExams } from "./types";

export const getExams = (utils: TUtils) => {
  return (utils.storage.getLocalStorage("exams") || []) as TExams;
};

export const addToExams = (newExams: TExams, utils: TUtils) => {
  const exams = getExams(utils);
  const oldExamIds: Set<number> = new Set(
    exams.map((i) => i.examTakingDetailID)
  );

  newExams.forEach((exam) => {
    if (!oldExamIds.has(exam.examTakingDetailID)) {
      exams.push(exam);
    }
  });

  utils.storage.setLocalStorage("exams", exams);
  return exams;
};

export const useExamChecker = (utils: TUtils) => {
  const { auth } = utils;
  const user = auth?.useAuth();
  const tabID = useTabId();

  const callback = useCallback(async () => {
    if (user?.typeId == UsersTypeEnum.STUDENT && tabID) {
      addToExams(await getExamInfo(utils, tabID), utils);
    }

    alertAndRedirectExam(utils);
  }, [user]);

  useEffect(() => {
    if (!user) return;
    utils.api
      .$get<{
        examTakingId: number;
        isExamSingleQuestion: boolean;
      }>("/exam/currentExam")
      .then((res) => {
        sendToExam(res.isExamSingleQuestion, res.examTakingId);
      })
      .catch((e: AxiosError) => {
        if (e.response?.status == 404) return Promise.resolve(false);
        return Promise.reject(e);
      });
  }, [user]);

  useEvery(callback, 30 * 1000, [callback]);
};

const sendToExam = (
  isExamSingleQuestion: boolean | undefined,
  examTakingId: any
) => {
  const path = isExamSingleQuestion
    ? `/#!examSingleQuestion;refID=${examTakingId}`
    : `/#!takeexam;refID=${examTakingId}`;

  Object.assign(window.location, { href: fixString(path, "EXAM_URL") });
};

const getExamInfo = (utils: TUtils, tabID: string) =>
  utils.api.$post<TExams>("/infrastructure/checkForNearFutureExam", null, {
    params: { tabID }
  });

export const alertAndRedirectExam = (utils: TUtils) => {
  const now = new Date();
  const inThreeMinutes = (() => {
    const d = new Date(now);
    d.setMinutes(d.getMinutes() + 3);
    return d;
  })();
  const exams = getExams(utils);

  const nearestExam = exams.find(
    (exam) => inThreeMinutes > new Date(exam.fireTime)
  );
  if (!nearestExam) return;

  const { isExamSingleQuestion, examTakingDetailID } = nearestExam;
  const examTime = new Date(nearestExam.fireTime);

  const diffSeconds = Math.round((Number(examTime) - Number(now)) / 1000);
  const diffMinutes = Math.round(diffSeconds / 60);
  if (diffSeconds <= 0) {
    exams.splice(exams.indexOf(nearestExam), 1);
    utils.storage.setLocalStorage("exams", exams);
    sendToExam(isExamSingleQuestion, examTakingDetailID);
    return;
  }
  if (diffSeconds <= 30)
    Toast?.info({ message: `${diffSeconds} ثانیه مانده به آزمون` });
  else if (diffMinutes <= 3)
    Toast?.info({ message: `${diffMinutes} دقیقه مانده به آزمون` });
};
